import apiInstance from './api';

/**
 * İşlem geçmişinde toplanacak en fazla sayfa sayısı (20 kayıt/sayfa → 500 kayıt).
 * Kaçak bir döngüye ya da beklenmedik büyüklükte bir geçmişe karşı güvenlik sınırı;
 * aşılırsa yanıt `truncated: true` ile işaretlenir ve UI kullanıcıyı uyarır (sessizce kırpılmaz).
 */
const MAX_HISTORY_PAGES = 25;

/**
 * Hydra sayfa IRI'sinden (`/api/...?page=4`) sayfa numarasını okur.
 * `hydra:last` yoksa (tek sayfalık yanıt) 1 döner.
 * @param {string} [iri]
 * @returns {number}
 */
const parsePageNumber = (iri) => {
  const match = /[?&]page=(\d+)/.exec(iri || '');
  return match ? Number(match[1]) : 1;
};

const mediaOfferApi = apiInstance.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Bir ArticleOffer'a ait tüm medya teklifi gruplarını getirir.
     * Her grup `currentVersion` (güncel versiyon) ve onun `lastDecision` (son karar)
     * alanını gömülü taşır.
     * @param {{ articleOffer: number|string }} args - ArticleOffer id'si
     */
    getMediaOfferGroups: build.query({
      query: ({ articleOffer }) => `/media_offer_groups/by/${articleOffer}`,
      transformResponse: (response) => response['hydra:member'],
      providesTags: ['MediaOffers'],
    }),
    /**
     * Tek bir medya teklifi grubunun detayını getirir.
     * Yanıt şeması liste ile aynıdır (`currentVersion` gömülü gelir).
     * @param {{ id: number|string }} args - Grup id'si
     */
    getMediaOfferGroupById: build.query({
      query: ({ id }) => `/media_offer_groups/${id}`,
      // Grup bazlı tag: revize yalnız ilgili grubu invalidate etsin (toplu refetch'i önler)
      providesTags: (result, error, { id }) => [{ type: 'MediaOfferDetail', id }],
    }),
    /**
     * Yeni medya teklifi oluşturur (yeni grup + ilk versiyon).
     * Gövde `group: { articleOffer, mediaType }` sarmalı + alanları içerir;
     * gövdeyi çağıran task hazırlar, servis yalnızca geçirir.
     * @param {{ body: object }} args - Hazırlanmış istek gövdesi
     */
    sendMediaOffer: build.mutation({
      query: ({ body }) => ({
        url: '/media_offers',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MediaOffers'],
    }),
    /**
     * Mevcut versiyon temel alınarak güncelleme gönderir — **düzenleme ve karar tek endpoint**.
     * Gövde alan düzenlemelerini ve/veya kararı (`reviewDecisionType`/`reviewDecisionNote`) taşır;
     * her ikisi de bağımsız opsiyoneldir (yalnız not da gönderilebilir — YM İmla/Editöryal). BE davranışı:
     * - Form değişti → yeni versiyon üretir; karar varsa yeni versiyona eklenir.
     * - Yalnızca karar/not → mevcut versiyona eklenir, yeni versiyon üretilmez.
     * - Ne form ne karar/not → `422 "No changes detected."`.
     * `group`/`createNote`/`mediaType` bu endpoint'te kullanılmaz.
     * @param {{ id: number|string, body: object, groupId?: number|string }} args - Hedef versiyon id'si,
     *   gövde ve (varsa) hedef grup id'si. `groupId` verilirse yalnız o grubun detayı/geçmişi
     *   invalidate edilir; verilmezse tüm `MediaOfferDetail` sorguları yenilenir (güvenli geri-düşüş).
     */
    reviseMediaOffer: build.mutation({
      query: ({ id, body }) => ({
        url: `/media_offers/${id}/revise`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { groupId }) => [
        'MediaOffers',
        groupId ? { type: 'MediaOfferDetail', id: groupId } : 'MediaOfferDetail',
      ],
    }),
    /**
     * Bir medya teklifi grubunu (ve tüm versiyonlarını) siler.
     * @param {{ id: number|string }} args - Grup id'si
     */
    deleteMediaOfferGroup: build.mutation({
      query: ({ id }) => ({
        url: `/media_offer_groups/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MediaOffers'],
    }),
    /**
     * Bir makale teklifine ait TÜM medya gruplarının olay-bazlı işlem geçmişini getirir
     * (İşlem Geçmişi modalı). Grup başına ayrı sorgu atılmaz; gruplama/filtreleme FE'de,
     * her kaydın taşıdığı `mediaOfferGroup` alanından yapılır.
     *
     * Her kayıt bir olaydır: `type` (created | field_change | media_added | media_removed | decision),
     * `version`, `field`, `from`/`to`, `actorRole`, `actorUser`, `occurredAt`, `mediaOfferGroup`.
     * Liste yeniden→eskiye sıralı döner. BE, kullanıcının rolüne göre görünürlüğü kendisi
     * filtreler; FE ekstra filtre uygulamaz.
     *
     * **Sayfalama:** BE 20'şerli sayfalar döndürür (`hydra:view`). Modaldeki grup ve olay-tipi
     * filtreleri client-side olduğu için tek sayfayla çalışmak filtreleri sessizce yanlış yapardı
     * ("Kararlar" → yalnız ilk sayfadaki kararlar). Bu yüzden sorgu **tüm sayfaları toplar**:
     * ilk sayfa alınır, `hydra:last`'tan sayfa sayısı okunur, kalanlar **paralel** çekilip sırayla
     * birleştirilir. UI sayfalamayı hiç görmez.
     *
     * RTK `merge`/`serializeQueryArgs` bilinçli olarak kullanılmadı: `MediaOffers` tag'i sık
     * invalidate edildiği için refetch mevcut sayfayı tekrar append edip kayıtları çiftlerdi.
     * Tek `queryFn` içinde toplamak bu sınıf hatayı tamamen ortadan kaldırır.
     *
     * `providesTags: ['MediaOffers']` bilinçli: oluşturma/revize/karar/silme mutasyonlarının hepsi
     * bu tag'i invalidate eder, dolayısıyla hangi grupta işlem yapılırsa yapılsın geçmiş tazelenir
     * (grup-bazlı `MediaOfferDetail` tag'i madde-düzeyi akış için yetersiz kalırdı).
     *
     * @param {{ articleOffer: number|string }} args - ArticleOffer id'si
     * @returns {{ entries: Object[], total: number, truncated: boolean }} `entries` tüm sayfaların
     *   birleşimi (her kayda çakışmayan bir `__uid` eklenir — BE'nin `@id`'si sayfa içi sıra
     *   numarası olduğundan sayfalar arasında tekrar eder ve React key olarak kullanılamaz).
     *   `truncated`, güvenlik sınırına (`MAX_HISTORY_PAGES`) takılıp eksik kaldıysa true olur.
     */
    getMediaOfferHistoryByArticle: build.query({
      async queryFn({ articleOffer }, _api, _extraOptions, fetchWithBQ) {
        const path = `/media_offer_history/by_article/${articleOffer}`;
        // Kaydın kendi @id'si sayfa içi index (0..19) → sayfalar arasında tekrarlıyor.
        // Stabil ve benzersiz bir React key için sayfa numarasıyla birleştiriyoruz.
        const withUid = (entries, page) => entries.map((entry, index) => ({ ...entry, __uid: `${page}-${index}` }));

        const first = await fetchWithBQ(`${path}?page=1`);
        if (first.error) return { error: first.error };

        const entries = withUid(first.data['hydra:member'] || [], 1);
        const total = first.data['hydra:totalItems'] ?? entries.length;
        const lastPage = parsePageNumber(first.data['hydra:view']?.['hydra:last']);
        const targetPage = Math.min(lastPage, MAX_HISTORY_PAGES);

        // Kalan sayfalar paralel çekilir; sıra korunacak şekilde birleştirilir.
        if (targetPage > 1) {
          const rest = await Promise.all(
            Array.from({ length: targetPage - 1 }, (_, index) => fetchWithBQ(`${path}?page=${index + 2}`))
          );
          const failed = rest.find((result) => result.error);
          if (failed) return { error: failed.error };
          rest.forEach((result, index) => entries.push(...withUid(result.data['hydra:member'] || [], index + 2)));
        }

        return { data: { entries, total, truncated: lastPage > MAX_HISTORY_PAGES } };
      },
      providesTags: ['MediaOffers'],
    }),
  }),
  overrideExisting: true,
});

export default mediaOfferApi;
